// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getAuthToken } from "@/api/client";
import type { components } from "@/api/schema";

// --- Types ---

export type ImportEntry = components["schemas"]["ImportEntryResponse"];
export type ImportStoreRef = components["schemas"]["StoreResponse"];
export type ImportChainRef = components["schemas"]["ChainsRelevantResponse"];
export type ImportUserRef = components["schemas"]["UserRelevantResponse"];
export type ImportVendorRef = components["schemas"]["VendorRelevantResponse"];

export interface ImportsListResult {
  imports: ImportEntry[];
  chains: ImportChainRef[];
  stores: ImportStoreRef[];
  vendors: ImportVendorRef[];
  users: ImportUserRef[];
  count: number;
}

export type EtlProcessing = components["schemas"]["ImportAssistedEtlProcessingsResponse"];
export type EtlInputFile = components["schemas"]["ImportAssistedEtlProcessingInputFileResponse"];

export type ValidateResponse = components["schemas"]["ImportAssistedValidateResponse"];
export type PrepareResponse = components["schemas"]["ImportAssistedPrepareResponse"];
export type SubmitResponse = components["schemas"]["ImportAssistedSubmitResponse"];

// --- Imports list (unified ingress endpoint) ---

interface ImportsListParams {
  page?: number;
  limit?: number;
  sortBy?: components["schemas"]["IngressImportsSortByEnum"];
  orderBy?: "asc" | "desc";
  search?: string;
  chainName?: string;
  storeName?: string;
  importType?: string;
  status?: string;
  source?: string;
  enabled?: boolean;
}

export function useImportsListQuery(params: ImportsListParams = {}) {
  const {
    page = 1,
    limit = 20,
    sortBy,
    orderBy,
    search,
    chainName,
    storeName,
    importType,
    status,
    source,
    enabled,
  } = params;

  return useQuery({
    queryKey: [
      "imports",
      "list",
      { page, limit, sortBy, orderBy, search, chainName, storeName, importType, status, source },
    ],
    enabled,
    queryFn: async () => {
      const res = await api.GET("/v1/ingress/imports", {
        params: {
          query: {
            page,
            limit,
            sortBy,
            orderBy,
            search: search || undefined,
            "filter[chainName]": chainName || undefined,
            "filter[storeName]": storeName || undefined,
            "filter[importType]": importType || undefined,
            "filter[status]": status || undefined,
            "filter[source]": source || undefined,
          },
        },
      });
      return res.data as unknown as ImportsListResult;
    },
  });
}

// --- Assisted imports ETL processings ---

export type EtlSortBy = components["schemas"]["EtlProcessingSortByEnum"];

interface EtlProcessingsParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: EtlSortBy;
  orderBy?: "asc" | "desc";
  chainName?: string;
  storeName?: string;
  onlyActive?: boolean;
  onlyApplicable?: boolean;
  enabled?: boolean;
}

export interface EtlProcessingsResult {
  etlProcessings: EtlProcessing[];
  chains: components["schemas"]["ChainsRelevantResponse"][];
  stores: components["schemas"]["StoresRelevantResponse"][];
  count: number;
}

export function useTransformationsQuery(params: EtlProcessingsParams = {}) {
  const {
    page = 1,
    limit = 20,
    search,
    sortBy,
    orderBy,
    chainName,
    storeName,
    onlyActive,
    onlyApplicable,
    enabled,
  } = params;

  return useQuery({
    queryKey: [
      "imports",
      "etl-processings",
      { page, limit, search, sortBy, orderBy, chainName, storeName, onlyActive, onlyApplicable },
    ],
    enabled,
    queryFn: async () => {
      const res = await api.GET("/v1/ingress/transformations/", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[chainName]": chainName || undefined,
            "filter[storeName]": storeName || undefined,
            "filter[onlyActive]": onlyActive,
            "filter[onlyApplicable]": onlyApplicable,
          },
        },
      });
      return res.data as unknown as EtlProcessingsResult;
    },
  });
}

// --- Single ETL processing detail ---

export type EtlProcessingDetail = components["schemas"]["ImportAssistedEtlProcessingDetailsResponse"];
export type EtlInputFileDetail = components["schemas"]["ImportAssistedEtlProcessingInputFileDetailsResponse"];

export function useEtlProcessingDetailQuery(etlId: number) {
  return useQuery({
    queryKey: ["imports", "etl-processing", etlId],
    queryFn: async () => {
      const res = await api.GET("/v1/ingress/transformations/{transformation_id}", {
        params: { path: { transformation_id: etlId } },
      });
      return res.data as unknown as EtlProcessingDetail;
    },
    enabled: etlId > 0,
  });
}

// --- ETL processing mutations ---

export function useToggleEtlActiveMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { etlProcessingId: number; isActive: boolean }) => {
      const res = await api.PATCH("/v1/ingress/transformations/{transformation_id}/active", {
        params: { path: { transformation_id: params.etlProcessingId } },
        body: { isActive: params.isActive },
      });
      if (res.error) throw new Error("Failed to update transformation status");
      return res.data as unknown as components["schemas"]["StatusResponse"];
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["imports", "etl-processings"] });
      queryClient.invalidateQueries({ queryKey: ["imports", "etl-processing", variables.etlProcessingId] });
    },
  });
}

// --- Assisted imports mutations ---

export function useStartImportSessionMutation() {
  return useMutation({
    mutationFn: async (params: {
      transformationId: number;
      chainName?: string;
      storeName?: string;
      /** Allow starting a session against an inactive transformation. Defaults to false. */
      allowInactive?: boolean;
    }) => {
      const res = await api.POST("/v1/ingress/assisted/start", {
        params: {
          query: {
            transformationId: params.transformationId,
            chainName: params.chainName,
            storeName: params.storeName,
            allowInactive: params.allowInactive ?? false,
          },
        },
      });
      if (res.error) throw new Error("Failed to start import session");
      return res.data as unknown as { importSessionId: string };
    },
  });
}

export function useUploadImportFileMutation() {
  return useMutation({
    mutationFn: async (params: {
      importSessionId: string;
      targetName: string;
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      const formData = new FormData();
      formData.append("uploadFile", params.file);

      const baseUrl = import.meta.env.VITE_API_URL;
      const url = `${baseUrl}/v1/ingress/assisted/upload?importSessionId=${encodeURIComponent(params.importSessionId)}&targetName=${encodeURIComponent(params.targetName)}`;

      return new Promise<unknown>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.setRequestHeader("Authorization", `Bearer ${getAuthToken()}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            params.onProgress?.(e.loaded / e.total);
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              resolve(null);
            }
          } else {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(formData);
      });
    },
  });
}

// --- Plain CSV import (no transformation, single store) ---

export type CsvImportType = components["schemas"]["IngressCsvImportTypeEnum"];
export type CsvImportResponse = components["schemas"]["IngressCsvImportResponse"];

/** Import types accepted by `POST /v1/ingress/csv`, in the order they are offered in the UI. */
export const CSV_IMPORT_TYPES: readonly CsvImportType[] = ["metadata", "boh", "sales", "pricing"];

/** A single entry of the backend's normalized error response (`{ status, code, errors: [...] }`). */
export interface ApiErrorEntry {
  type?: string;
  detail?: string;
  msg?: string;
}

/**
 * Failure of a CSV upload, carrying the backend's error entries. A 422 from the endpoint lists one
 * entry per offending row/column, which the dialog renders so the user can fix the file.
 */
export class CsvImportError extends Error {
  readonly entries: ApiErrorEntry[];

  constructor(entries: ApiErrorEntry[], fallbackMessage: string) {
    super(entries.find((e) => e.detail || e.msg)?.detail ?? fallbackMessage);
    this.name = "CsvImportError";
    this.entries = entries;
  }
}

/**
 * Uploads a CSV file straight to the back-office API, which converts it into entities of the chosen
 * import type and submits an import request for the store. Uses XHR (not `api.POST`) so the dialog
 * can show upload progress; error bodies are unwrapped here since the client middleware is bypassed.
 */
export function useImportCsvMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      storeName: string;
      importType: CsvImportType;
      file: File;
      /** Replace the store code carried by the file with the store's own, instead of rejecting it. */
      overrideStoreCode?: boolean;
      onProgress?: (progress: number) => void;
    }) => {
      const formData = new FormData();
      formData.append("csvFile", params.file);

      const query = new URLSearchParams({
        storeName: params.storeName,
        importType: params.importType,
        overrideStoreCode: String(params.overrideStoreCode ?? false),
      });
      const url = `${import.meta.env.VITE_API_URL}/v1/ingress/csv?${query}`;

      return new Promise<CsvImportResponse>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.setRequestHeader("Authorization", `Bearer ${getAuthToken()}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) params.onProgress?.(e.loaded / e.total);
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText) as CsvImportResponse);
            } catch {
              reject(new CsvImportError([], `Upload failed (${xhr.status})`));
            }
            return;
          }
          let entries: ApiErrorEntry[] = [];
          try {
            entries = (JSON.parse(xhr.responseText) as { errors?: ApiErrorEntry[] }).errors ?? [];
          } catch {
            // Non-JSON body (e.g. a proxy error page) — fall back to the status code alone.
          }
          reject(new CsvImportError(entries, `Upload failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new CsvImportError([], "Upload failed"));
        xhr.send(formData);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["imports"] });
    },
  });
}

export function useValidateImportMutation() {
  return useMutation({
    mutationFn: async (importSessionId: string) => {
      const res = await api.POST("/v1/ingress/assisted/validate", {
        params: { query: { importSessionId } },
      });
      if (res.error) throw new Error("Failed to validate import");
      return res.data as unknown as ValidateResponse;
    },
  });
}

export function usePrepareImportMutation() {
  return useMutation({
    mutationFn: async (importSessionId: string) => {
      const res = await api.POST("/v1/ingress/assisted/prepare", {
        params: { query: { importSessionId } },
      });
      if (res.error) throw new Error("Failed to prepare import");
      return res.data as unknown as PrepareResponse;
    },
  });
}

// --- Download import file ---

/**
 * Resolves an import file's storage path to a short-lived public URL via
 * `GET /v1/ingress/download/{url}`. The `fileUrl` argument is the path carried on
 * an `ImportEntry.files[].url` (the `json`/`csv` variants); it is appended raw to
 * the endpoint — the value is itself a slash-bearing path, so we bypass
 * openapi-fetch here to avoid it percent-encoding the slashes. The endpoint
 * returns a `redirectUrl` pointing at the actual file in the imports bucket.
 */
export function useDownloadImportFile() {
  return useMutation({
    mutationFn: async (fileUrl: string) => {
      const baseUrl = import.meta.env.VITE_API_URL;
      const res = await fetch(`${baseUrl}/v1/ingress/download/${fileUrl}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) {
        // Surface the backend's error detail (e.g. "Blob signing is not yet supported
        // for AUTHORIZED_USER tokens" in local dev) rather than a generic failure.
        let detail = "";
        try {
          const body = (await res.json()) as { errors?: Array<{ detail?: string }>; detail?: string };
          detail = body.errors?.find((e) => e?.detail)?.detail ?? body.detail ?? "";
        } catch {
          // non-JSON body — leave detail empty, caller falls back to a generic message
        }
        throw new Error(detail);
      }
      const data = (await res.json()) as components["schemas"]["StatusRedirectResponse"];
      return data.redirectUrl;
    },
  });
}

// --- Retrigger import ---

export function useTriggerImportMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { importId: number; includeSubsequent?: boolean }) => {
      const res = await api.POST("/v1/ingress/trigger", {
        params: {
          query: {
            importId: params.importId,
            includeSubsequent: params.includeSubsequent ?? false,
          },
        },
      });
      if (res.error) {
        const errObj = res.error as { errors?: Array<{ detail?: string }> } | undefined;
        const detail = errObj?.errors?.find((e) => e?.detail)?.detail;
        throw new Error(detail ?? "Failed to retrigger import");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["imports"] });
    },
  });
}

export function useSubmitImportMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { importSessionId: string; ignoredPreparedImportFileId?: number[] }) => {
      const res = await api.POST("/v1/ingress/assisted/submit", {
        params: { query: { importSessionId: params.importSessionId } },
        body: { ignoredPreparedImportFileId: params.ignoredPreparedImportFileId ?? [] },
      });
      if (res.error) throw new Error("Failed to submit import");
      return res.data as unknown as SubmitResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chains"] });
      queryClient.invalidateQueries({ queryKey: ["stores"] });
    },
  });
}
